/* eslint-disable no-mixed-spaces-and-tabs */
import storeAxios from "@/store/axios";
import { isNil } from "@/utils";
import toBackend from "./simulations/dataMappers/simulationToBackend";
import simulationDefaults from "./simulations/models/simulationDefaults";
import simulationOptions from "./simulations/models/simulationOptions";
import Simulation from "./simulations/models/simulation";

export default {
	namespaced: true,

	state() {
		return {
			simulationOptions,
			simulationDefaults,
			simulation: null,
		};
	},

	mutations: {
		destroy(state) {
			state.simulation = null;
		},

		setSimulation(state, simulation) {
			state.simulation = simulation;
		},

		setSimulationValues(state, values) {
			const oldValues = state.simulation.values;

			const isFirstSet = isNil(oldValues);

			// this all mutate the original values object
			if (isFirstSet) {
				clampLoads(values);
				clampSnow(values);
				clampLocks(values);
				adjustLocks(values);
			} else {
				const isTotalSpansChanged =
					getTotalSpans(values) !== getTotalSpans(oldValues);

				if (isTotalSpansChanged) {
					clampLoads(values);
					clampSnow(values);
					clampLocks(values);
				}

				const isTypeChanged = oldValues.type !== values.type;

				const isLocksNeedingAdjustment =
					isTypeChanged ||
					oldValues.profileOrientation !== values.profileOrientation ||
					oldValues.profileFormat !== values.profileFormat;

				if (isLocksNeedingAdjustment) {
					adjustLocks(values);
				}

				const isSpansLengthChanged =
					getSpansLengthSum(values.spans) !==
					getSpansLengthSum(oldValues.spans);

				if (isSpansLengthChanged) {
					adjustWindAndSnowLengths(values);
				}
			}

			state.simulation.values = values;
		},

		setSimulationName(state, name) {
			state.simulation.name = name;
		},

		setSimulationProject(state, project) {
			state.simulation.project = project;
		},

		setSimulationSteelGrades(state, steelGrades) {
			state.simulation.steelGrades = steelGrades;
		},
	},

	actions: {
		init({ commit, dispatch }, simulationId) {
			commit("destroy");

			return new Promise((resolve, reject) => {
				if (simulationId === "new") {
					commit("setSimulation", new Simulation());
					resolve();
				} else {
					dispatch("simulations/fetchSimulation", simulationId, {
						root: true,
					})
						.then((simulation) => {
							commit("setSimulation", new Simulation(simulation));
						})
						.catch((err) => {
							console.log(err);
							reject(err);
						})
						.finally(() => {
							resolve();
						});
				}
			});
		},

		async saveMetadata({ state, commit, dispatch }) {
			return dispatch(
				"simulations/updateSimulationMetadata",
				{
					id: state.simulation.id,
					name: state.simulation.name,
					description: state.simulation.description,
					project: state.simulation.project,
					chosenResultId: state.simulation.chosenResultId,
				},
				{
					root: true,
				}
			).then((simulation) => {
				commit("setSimulation", new Simulation(simulation));
			});
		},

		async save({ state, commit, dispatch }) {
			state.simulation.date = new Date().toISOString();

			let action, payload;

			if (state.simulation.id == "new") {
				action = "simulations/createSimulation";
				payload = {
					simulation: state.simulation,
				};
			} else {
				action = "simulations/updateSimulation";
				payload = {
					id: state.simulation.id,
					simulation: state.simulation,
				};
			}

			return dispatch(action, payload, {
				root: true,
			}).then((simulation) => {
				commit("setSimulation", new Simulation(simulation));
			});
		},

		async initCalculation({ dispatch, commit, state }) {
			return dispatch(
				"simulations/initCalculation",
				{ id: state.simulation.id },
				{ root: true }
			);
		},
	},

	getters: {
		originalSimulation(state, getters, rootState, rootGetters) {
			return rootGetters["simulations/getSimulation"](state.simulation.id);
		},

		getOption(state) {
			return (simulationOptionsKey, optionId) => {
				return state.simulationOptions[simulationOptionsKey].options.find(
					({ id }) => id === optionId
				);
			};
		},

		getMainSpanDescriptions() {
			return (spans) => {
				return spans.main.map((spanItem, i) => ({
					label: i + 1,
					longLabel: () => ["Vão ${num}", { num: i + 1 }],
					spanNumber: i + 1,
					details: {
						spanType: "main",
						spanIndex: i,
					},
					id: `span:${i}`,
				}));
			};
		},

		mainSpanDescriptions(state, getters) {
			return getters.getMainSpanDescriptions(state.simulation.values.spans);
		},

		consoleSpanDescriptions(state) {
			if (state.simulation.values.spans.useConsole) {
				return [
					{
						label: "C1",
						longLabel: () => ["Consola ${num}", { num: 1 }],
						details: {
							spanType: "console",
							spanIndex: 0,
						},
						id: "console:0",
					},
					{
						label: "C2",
						longLabel: () => ["Consola ${num}", { num: 2 }],
						details: {
							spanType: "console",
							spanIndex: 1,
						},
						id: "console:1",
					},
				];
			} else {
				return [];
			}
		},
		// main + console
		spanDescriptions(state, getters) {
			if (state.simulation.values.spans.useConsole) {
				return [
					getters.consoleSpanDescriptions[0],
					...getters.mainSpanDescriptions,
					getters.consoleSpanDescriptions[1],
				];
			}

			return getters.mainSpanDescriptions;
		},

		spansLengthSum(state) {
			return getSpansLengthSum(state.simulation.values.spans);
		},

		getMaxForLengthField(state, getters) {
			return ({ lengthFields, idx }) => {
				const otherFieldsSum = lengthFields.reduce((result, val, _idx) => {
					if (idx === _idx) {
						return result;
					} else {
						return result + val;
					}
				}, 0);

				return getters.spansLengthSum - otherFieldsSum;
			};
		},
	},
};

function getSpansLengthSum(spans) {
	const { useConsole, main, console } = spans;

	return [...main, ...(useConsole ? console : [])].reduce(
		(result, { size }) => {
			return result + size;
		},
		0
	);
}

function adjustWindAndSnowLengths(values) {
	const spansLengthSum = getSpansLengthSum(values.spans);

	[
		values.snow.snowAccumulation.l,
		values.wind.general.pressure.l,
		values.wind.general.suction.l,
		values.wind.local.pressure.l,
		values.wind.local.suction.l,
	].forEach((lItems) => {
		const lItemsSum = lItems.reduce((result, item) => result + item, 0);
		if (lItemsSum > spansLengthSum) {
			// SHOULD FETCH FROM DEFAULTS
			lItems.splice(0, lItems.length, ...[0, 0, 0]);
		}
	});

	console.log(
		"🚀 ~ adjustWindAndSnowLengths ~ values.snow.snowAccumulation.l:",
		values.snow.snowAccumulation.l
	);
}

function clampLoads(values) {
	clampPermanentDistributedLoads(values);
	clampLiveDistributedLoads(values);
	clampOcassionalLoads(values);
}

function clampPermanentDistributedLoads(values) {
	const spanMappedValues = values.loads.permanent.distributed;

	clampSpanMappedValues(spanMappedValues, {
		useConsole: values.spans.useConsole,
		totalMainSpans: values.spans.main.length,
		getValue: () => {
			if (!spanMappedValues.cfgMode.isSpanBySpan) {
				const sampleItem = spanMappedValues.main[0];

				if (sampleItem) {
					return { ...sampleItem };
				}
			}

			return { min: 1, max: 1 };
		},
	});
}

function clampLiveDistributedLoads(values) {
	const spanMappedValues = values.loads.live.distributed;

	clampSpanMappedValues(spanMappedValues, {
		useConsole: values.spans.useConsole,
		totalMainSpans: values.spans.main.length,
		getValue: () => {
			if (!spanMappedValues.cfgMode.isSpanBySpan) {
				const sampleItem = spanMappedValues.main[0];
				if (sampleItem) {
					return sampleItem;
				}
			}

			return 1;
		},
	});
}

function clampOcassionalLoads(values) {
	const useConsole = values.spans.useConsole;
	const totalMainSpans = values.spans.main.length;
	const maxMainSpanIdx = totalMainSpans - 1;

	[values.loads.permanent.point, values.loads.live.point].forEach((point) => {
		point.forEach((item) => {
			if (!useConsole) {
				item.targetSpans = item.targetSpans.filter(
					(spanId) => !spanId.startsWith("console")
				);
			}

			item.targetSpans = item.targetSpans.filter((spanId) => {
				const [spanType, spanIdxAsStr] = spanId.split(":");
				const spanIdx = +spanIdxAsStr;

				if (spanType === "main") {
					if (spanIdx > maxMainSpanIdx) {
						return false;
					}
				}

				return true;
			});
		});
	});
}

function clampSnow(values) {
	[values.snow.general, values.snow.exceptional].forEach((spanMappedValues) => {
		clampSpanMappedValues(spanMappedValues, {
			useConsole: values.spans.useConsole,
			totalMainSpans: values.spans.main.length,
			getValue: () => 1,
		});
	});
}

function clampLocks(values) {
	[
		values.locks.upperFibre.lateralLocks,
		values.locks.lowerFibre.lateralLocks,
	].forEach((spanMappedValues) => {
		clampSpanMappedValues(spanMappedValues, {
			useConsole: values.spans.useConsole,
			totalMainSpans: values.spans.main.length,
			getValue: () => 1,
		});
	});
}

//generic
function clampSpanMappedValues(
	spanMappedValues,
	{ useConsole, totalMainSpans, getValue }
) {
	// type spanMappedValues {
	// 	main,
	// 	console,
	// 	cfgMode
	// }

	// main span values
	const newMainValues = spanMappedValues.main.slice(0, totalMainSpans);
	const totalMissing = totalMainSpans - newMainValues.length;

	if (totalMissing > 0) {
		newMainValues.push(
			...[...Array(totalMissing).keys()].map(() => getValue())
		);
	}

	spanMappedValues.main = newMainValues;

	// console span values
	if (!useConsole) {
		spanMappedValues.console = [];
	} else {
		spanMappedValues.console = [
			getValue(),
			getValue(),
			...spanMappedValues.console,
		].slice(0, 2);
	}
}

function adjustLocks(values) {
	const { type, profileOrientation, profileFormat } = values;

	if (["ROOF", "GENERAL"].includes(type)) {
		values.locks.upperFibre.type = 0;
		values.locks.lowerFibre.use = false;
	} else if (type === "FACADE") {
		values.locks.lowerFibre.use = true;

		if (profileOrientation === "VERTICAL") {
			values.locks.upperFibre.type = 0;
			setSpanMappedValuesToSingleValue(values.locks.lowerFibre.lateralLocks, 1);
		} else if (profileOrientation === "HORIZONTAL") {
			["main", "console"].forEach((spanType) => {
				values.spans[spanType].forEach(({ size }, i) => {
					values.locks.upperFibre.lateralLocks[spanType][
						i
					] = getLateralLocksDefaultValue(size);
				});
			});

			if (profileFormat === "MAJOR_AXIS_BENDING_REGULAR") {
				values.locks.upperFibre.type = 1;
				setSpanMappedValuesToSingleValue(
					values.locks.upperFibre.lateralLocks,
					0
				);
			} else if (profileFormat === "MAJOR_AXIS_BENDING_INVERTED") {
				values.locks.upperFibre.type = 0;
			}
		}
	}
}

function setSpanMappedValuesToSingleValue(spanMappedValues, value) {
	spanMappedValues.main = spanMappedValues.main.map(() => value);
	spanMappedValues.console = spanMappedValues.console.map(() => value);
	spanMappedValues.cfgMode.isSpanBySpan = false;
}

function getTotalSpans(values) {
	return values.spans.main.length + (values.spans.useConsole ? 2 : 0);
}

function getLateralLocksDefaultValue(spanSize) {
	const spanSizeArr = [3.5, 7, 10.5, 14, 17.5, 20.5];

	for (let i = 0; i < spanSizeArr.length; i++) {
		if (spanSize <= spanSizeArr[i]) {
			return i;
		}
	}
}
